import { useEffect, useState } from "react";
import {
  CreateInstitutionInput,
  Institution,
  UpdateInstitutionInput
} from "../../API";
import { QueryStatus } from "../../app/types";
import {
  fetchInstitution,
  createInstitution as createInstitutionAPI,
  updateInstitution as updateInstitutionAPI,
  fetchAllInstitutions
} from "./institutionsAPI";
import { useDispatch } from "react-redux";
import {
  selectInstitutions,
  selectInstitutionsError,
  selectInstitutionsStatus,
  setInstitutions,
  setInstitutionsError,
  setInstitutionsStatus
} from "./institutionsSlice";
import { useAppSelector } from "../../app/hooks";

export const useInstitutionById = (id?: string) => {
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getInstitutionById = async (id: string) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchInstitution(id);
      setInstitution(result);
      setStatus("succeeded");
    } catch (error) {
      setStatus("failed");
      setError(`Error loading institution: ${error}`);
    }
  };

  useEffect(() => {
    if (id) {
      getInstitutionById(id);
    }
  }, [id]);

  return {
    institution,
    status,
    error
  };
};

export const useUpdateInstitution = () => {
  const [updatedInstitution, setUpdatedInstitution] =
    useState<Institution | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateInstitution = async (input: UpdateInstitutionInput) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await updateInstitutionAPI(input);
      setUpdatedInstitution(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error updating institution:", error);
      setStatus("failed");
      setError(`Error updating institution: ${JSON.stringify(error)}`);
    }

    return null;
  };

  return {
    updatedInstitution,
    status,
    error,
    updateInstitution
  };
};

export const useAllInstitutions = () => {
  // Try to get from Redux first (for SysAdmins who load in auth flow)
  const reduxInstitutions = useAppSelector(selectInstitutions);
  const reduxStatus = useAppSelector(selectInstitutionsStatus);
  const reduxError = useAppSelector(selectInstitutionsError);

  // Local state for ProgramAdmins who load on demand
  const [localInstitutions, setLocalInstitutions] = useState<Institution[] | null>(null);
  const [localStatus, setLocalStatus] = useState<QueryStatus>("idle");
  const [localError, setLocalError] = useState<string | null>();

  const dispatch = useDispatch();

  const getAllInstitutions = async () => {
    // If already loaded or currently loading in Redux, don't reload
    if (reduxStatus === "loading" || reduxStatus === "succeeded") {
      return reduxInstitutions;
    }

    // Otherwise, load and store in Redux
    dispatch(setInstitutionsStatus("loading"));
    dispatch(setInstitutionsError(undefined));

    try {
      const results = await fetchAllInstitutions();
      dispatch(setInstitutions(results));
      dispatch(setInstitutionsStatus("succeeded"));
      return results;
    } catch (error) {
      console.error("Error loading institutions:", error);
      dispatch(setInstitutionsStatus("failed"));
      dispatch(setInstitutionsError(`Error loading institutions: ${error}`));
      throw error;
    }
  };

  // No auto-load on mount - institutions are loaded in auth flow for SysAdmins
  // Components that need institutions should rely on Redux state or call getAllInstitutions() explicitly
  // For ProgramAdmins: they should call getAllInstitutions() when needed

  // Return Redux state if available, otherwise local state
  return {
    institutions: reduxInstitutions.length > 0 ? reduxInstitutions : localInstitutions,
    status: reduxStatus !== "idle" ? reduxStatus : localStatus,
    error: reduxError || localError,
    getAllInstitutions
  };
};

export const useCreateInstitution = () => {
  const [newInstitution, setNewInstitution] = useState<Institution | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createInstitution = async (input: CreateInstitutionInput) => {
    setStatus("loading");
    setError(null);

    try {
      const creationResult = await createInstitutionAPI(input);
      setNewInstitution(creationResult);
      setStatus("succeeded");
    } catch (error) {
      console.error("error fetching institutions:", error);
      setStatus("failed");
      setError(`Error loading institutions: ${error}`);
    }
  };

  return {
    newInstitution,
    status,
    error,
    createInstitution
  };
};

export const alphabetizeInstitutions = (institutions: Institution[]) => {
  return [...institutions]?.sort((a, b) => {
    const institutionNameA = a.name?.toLowerCase();
    const institutionNameB = b.name?.toLowerCase();
    if (institutionNameA && institutionNameB) {
      if (institutionNameA < institutionNameB) {
        return -1;
      }
      if (institutionNameA > institutionNameB) {
        return 1;
      }
    }
    return 0;
  });
};

export const InstitutionFormErrors = {
  EmptyInstitutionName: "Please enter a value for the Institution Name",
  CreationError:
    "There was an error creating this Institution. Please try again or contact your administrator."
};

export const InstitutionNetworkErrors = {
  FetchAllInstitutionsError:
    "Error fetching institutions. Please try again or contact your administrator.",
  FetchInstitutionError:
    "Error fetching institution. Please try again or contact your administrator.",
  InstitutionNotFound: "Institution not found!",
  UpdateInstitutionError:
    "Error updating institution. Please try again or contact your administrator."
};

export const InstitutionNotifications = {
  NoInstitutions: "No institutions exist on the system."
};
